import React, { useEffect, useState } from 'react';
import {
  Form,
  FormInstance,
  SelectBooleanInput
} from '@lightbeamhealth/blueprint-form';

import { CardBlock } from '../../card';
import { LoadingBlock } from '../../loading/progress';
import Modal from '../../modal';
import Toast from '../../toast';
import Callout from '../../callout';

import { Col, Row } from '../../../../grid';
import Button from '../../../molecules/button';
import Icon from '../../../atoms/icon';
import { faSave } from '@fortawesome/pro-solid-svg-icons';
import { ConfigComponent } from '../../../../libs/apps';
import { PostJSON, BaseResponse } from '../../../../libs/request';
import { useGetAccountSettings } from '../../../../libs/account';

interface EpxGenericConfigFormData {
  programCommentFlow: {
    [key: string]: boolean;
  };
}

/**
 * General Medical Program Configuration Form
 *
 * @returns JSX.Element
 */
export const EpxGenericConfigForm: ConfigComponent = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [settings, setSettings] = useState<EpxGenericConfigFormData | null>(
    null
  );
  const { settings: existingSettings, isLoading: isDataLoading } =
    useGetAccountSettings();
  const appName = 'epxgeneric';
  let initialValue = false;
  if (
    existingSettings &&
    existingSettings?.programCommentFlow &&
    existingSettings?.programCommentFlow[appName]
  ) {
    initialValue = existingSettings.programCommentFlow[appName];
  }
  const initialFormData = {
    programCommentFlow: {
      [appName]: initialValue
    }
  };
  const form = new FormInstance<EpxGenericConfigFormData>(initialFormData);

  useEffect(() => {
    if (settings != null) {
      form.setData(settings);
    }
  }, [settings]);

  const onSubmit = (data: EpxGenericConfigFormData) => {
    const params = {
      programCommentFlow: {
        [appName]: data.programCommentFlow[appName]
      }
    };
    setIsLoading(true);
    PostJSON<BaseResponse>({
      url: '/user/edit_account_settings/',
      params
    }).then((response) => {
      setIsLoading(false);
      if (response.status !== 'OK') {
        Modal.error({
          title: 'Error Saving Settings',
          content: response.message
        });
        return;
      }
      Toast({
        intent: 'success',
        message: 'The settings have been saved successfully!'
      });
      setSettings(data);
    });
  };

  if (isLoading) {
    return <LoadingBlock />;
  }

  return isDataLoading || isLoading ? null : (
    <Form form={form} onSubmit={onSubmit}>
      <>
        <CardBlock>
          <Row>
            <Col sm={24}>
              <SelectBooleanInput
                label="Enable Program Comment Flow"
                name={`programCommentFlow[${appName}]`}
                fill
              />
              <Callout>
                Turns on an extra two-step SMS follow-up after completed
                check-ins: asking if there are other issues and collecting a
                freeform response.
              </Callout>
            </Col>
          </Row>
          <Row className="mt-7">
            <Col sm={8}>
              <Button
                type="submit"
                icon={<Icon icon={faSave} />}
                intent="primary"
                fill
              >
                Save
              </Button>
            </Col>
          </Row>
        </CardBlock>
      </>
    </Form>
  );
};
